import { Space } from "@mantine/core";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import walletIcon from "../assets/images/wallet.svg";
import { useTokenDetails } from "../context/token-details-context";
import { formatAmountWithDecimals } from "../lib/utils";
import { Paragraph, ParagraphBold, Title } from "./styled";

export const WalletInfo = () => {
	const { t } = useTranslation();

	const { walletSummaryDetails, isLoadingWalletTokenDetails } =
		useTokenDetails();

	const wallets = walletSummaryDetails?.wallets || [];

	const nonCirculatingSupply = walletSummaryDetails?.notInCirculation;
	const masterReserveWallet = wallets?.find(
		(wallet) => wallet?.category === "Master",
	);
	const distributionWallet = wallets?.find(
		(wallet) => wallet?.category === "Distribution",
	);
	const institutionWallet = wallets?.find(
		(wallet) => wallet?.category === "Institution",
	);

	const decimals = walletSummaryDetails?.decimals;
	const symbol = walletSummaryDetails?.symbol || "";

	return (
		<Wrapper>
			<SupplyWallets>
				<WalletField>
					<Label>{t("total.supply.description")}</Label>
					{!isLoadingWalletTokenDetails && (
						<WalletValue>
							{formatAmountWithDecimals(walletSummaryDetails?.supply, decimals)}{" "}
							{symbol}
						</WalletValue>
					)}
				</WalletField>
				<WalletField>
					<Label>{t("circulating.supply.description")}</Label>
					{!isLoadingWalletTokenDetails && (
						<WalletValue>
							{formatAmountWithDecimals(
								walletSummaryDetails?.inCirculation,
								decimals,
							)}{" "}
							{symbol}
						</WalletValue>
					)}
				</WalletField>
				<WalletField>
					<Label>{t("non.circulating.supply.description")}</Label>
					{!isLoadingWalletTokenDetails && (
						<WalletValue>
							{nonCirculatingSupply
								? formatAmountWithDecimals(nonCirculatingSupply, decimals)
								: "0"}{" "}
							{symbol}
						</WalletValue>
					)}
				</WalletField>
			</SupplyWallets>

			<AssetsSection>
				<SectionTitle>{t("circulating.supply.description")}</SectionTitle>
				<Space h={24} />
				<WalletCard>
					<AssetCardLeftSection>
						<AssetIcon>
							<img src={walletIcon} alt="wallet" />
						</AssetIcon>
						<div>
							<AssetLabel>{t("wallets.distribution")}</AssetLabel>
						</div>
					</AssetCardLeftSection>
					<div>
						<AssetLabel style={{ textAlign: "right" }}>
							{formatAmountWithDecimals(
								Number(distributionWallet?.balances?.[0]?.amount),
								decimals,
							) || 0}
							{symbol}
						</AssetLabel>
						{/* <AssetValue style={{ textAlign: 'right' }}>USD 300</AssetValue> */}
					</div>
				</WalletCard>

				<WalletCard>
					<AssetCardLeftSection>
						<AssetIcon>
							<img src={walletIcon} alt="wallet" />
						</AssetIcon>
						<div>
							<AssetLabel>{t("wallets.institution")}</AssetLabel>
						</div>
					</AssetCardLeftSection>
					<div>
						<AssetLabel style={{ textAlign: "right" }}>
							{formatAmountWithDecimals(
								Number(institutionWallet?.balances?.[0]?.amount),
								decimals,
							) || 0}{" "}
							{symbol}
						</AssetLabel>
					</div>
				</WalletCard>
			</AssetsSection>

			<AssetsSection>
				<SectionTitle>{t("non.circulating.supply.description")}</SectionTitle>
				<Space h={24} />
				<WalletCard>
					<AssetCardLeftSection>
						<AssetIcon>
							<img src={walletIcon} alt="wallet" />
						</AssetIcon>
						<div>
							<AssetLabel>{t("wallets.master")}</AssetLabel>
						</div>
					</AssetCardLeftSection>
					<div>
						<AssetLabel style={{ textAlign: "right" }}>
							{formatAmountWithDecimals(
								Number(masterReserveWallet?.balances?.[0]?.amount),
								decimals,
							) || 0}
							{symbol}
						</AssetLabel>
					</div>
				</WalletCard>
			</AssetsSection>
		</Wrapper>
	);
};

const SectionTitle = styled(Title)`
  text-transform: capitalize;
`;
const Wrapper = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.primary.powderBlue};
  margin-top: 16px;
`;

const WalletField = styled.div`
  justify-content: space-between;
  align-items: center;
  display: flex;
  margin-bottom: 8px;
`;

const Label = styled(Paragraph)`
  color: var(--textColor);
`;

const WalletValue = styled(ParagraphBold)`
  font-size: 14px;
  line-height: 21px;
`;

const AssetLabel = styled(WalletValue)`
  margin-bottom: 4px;
  line-height: 21px;
`;

const SupplyWallets = styled.div`
  margin-top: 24px;
`;

const AssetsSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.primary.powderBlue};
`;

const WalletCard = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AssetCardLeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const AssetIcon = styled.div`
  background: ${({ theme }) => theme.colors.secondary.blue};
  border-radius: 10px;
  width: 32px;
  height: 32px;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
`;
